import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, MailOpen, Mail, Trash2, Plus, Pencil, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vhe_admin_token";

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const inputCls =
  "w-full border border-white/15 bg-navy/60 px-4 py-3 text-sm text-parchment placeholder:text-slate-500 focus:border-gold focus:outline-none";

const emptyPost = { title: "", excerpt: "", body: "", tag: "Transformative Media", published: true };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="blueprint-grid relative flex min-h-screen items-center justify-center bg-navy px-4">
      <div className="crosshair left-6 top-8" aria-hidden="true" />
      <div className="crosshair bottom-8 right-6" aria-hidden="true" />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative w-full max-w-md border border-gold/25 bg-blueprint/80 p-8 backdrop-blur-md sm:p-10">
        <img src="/assets/vhe-logo.png" alt="Vision-Heirs Enterprises" className="mx-auto mb-6 h-14 w-auto" />
        <h1 className="text-center font-serif text-2xl font-bold text-parchment">Architect&rsquo;s Office</h1>
        <p className="mb-8 mt-2 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-gold/70">Private — Authorized Access Only</p>
        <label htmlFor="admin-email" className="mb-2 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Email</label>
        <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@vhesolutionsllc.org" />
        <label htmlFor="admin-password" className="mb-2 mt-5 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Password</label>
        <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••••" />
        {error && <p data-testid="admin-login-error" role="alert" className="mt-4 text-sm text-red-400">{error}</p>}
        <button type="submit" data-testid="admin-login-submit" disabled={busy} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-navy transition-colors hover:bg-goldlight disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Verifying…" : "Enter the Office"}
        </button>
        <a href="/" data-testid="admin-back-to-site" className="mt-6 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-gold">← Back to site</a>
      </form>
    </div>
  );
};

const Enquiries = () => {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/enquiries`, authHeader()).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const toggleRead = async (item) => {
    await axios.patch(`${API}/enquiries/${item.id}`, { read: !item.read }, authHeader());
    load();
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete enquiry from ${item.name}?`)) return;
    await axios.delete(`${API}/enquiries/${item.id}`, authHeader());
    toast.success("Enquiry deleted.");
    load();
  };

  if (items === null) return <p className="py-16 text-center font-mono text-xs text-slate-400">Loading…</p>;
  if (!items.length) return <p data-testid="enquiries-empty" className="py-16 text-center font-serif text-xl italic text-slate-400">No enquiries yet — the blueprint awaits its first visitor.</p>;

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((item) => (
        <article key={item.id} data-testid={`enquiry-item-${item.id}`} className={`border p-5 transition-colors sm:p-6 ${item.read ? "border-white/10 bg-blueprint/40" : "border-gold/40 bg-blueprint/80"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-serif text-lg font-semibold text-parchment">
                {item.name}
                {!item.read && <span className="ml-3 rounded-full bg-gold px-2.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.2em] text-navy">New</span>}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                {item.email}{item.organization ? ` · ${item.organization}` : ""} · {item.interest}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => toggleRead(item)} data-testid={`enquiry-read-toggle-${item.id}`} aria-label={item.read ? "Mark as unread" : "Mark as read"} className="rounded-full border border-white/20 p-2 text-slate-300 transition-colors hover:border-gold hover:text-gold">
                {item.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => remove(item)} data-testid={`enquiry-delete-${item.id}`} aria-label="Delete enquiry" className="rounded-full border border-white/20 p-2 text-slate-300 transition-colors hover:border-red-400 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-300">{item.message}</p>
          <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">
            {new Date(item.timestamp).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
          </p>
        </article>
      ))}
    </div>
  );
};

const JournalAdmin = () => {
  const [posts, setPosts] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/journal`, authHeader()).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);
  useEffect(load, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (draft.id) {
        await axios.put(`${API}/admin/journal/${draft.id}`, draft, authHeader());
        toast.success("Article updated.");
      } else {
        await axios.post(`${API}/admin/journal`, draft, authHeader());
        toast.success("Article published.");
      }
      setDraft(null);
      load();
    } catch {
      toast.error("Could not save the article.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await axios.delete(`${API}/admin/journal/${p.id}`, authHeader());
    toast.success("Article deleted.");
    load();
  };

  const togglePublish = async (p) => {
    await axios.put(`${API}/admin/journal/${p.id}`, { title: p.title, excerpt: p.excerpt, body: p.body, tag: p.tag, published: !p.published }, authHeader());
    load();
  };

  return (
    <div data-testid="journal-admin">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-parchment">Journal Articles</h2>
        <button type="button" onClick={() => setDraft({ ...emptyPost })} data-testid="journal-new-button" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-navy transition-colors hover:bg-goldlight">
          <Plus className="h-4 w-4" /> New Article
        </button>
      </div>

      {draft && (
        <form onSubmit={save} data-testid="journal-editor-form" className="mb-8 space-y-4 border border-gold/30 bg-blueprint/60 p-6">
          <input required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Article title" data-testid="journal-title-input" className={inputCls} />
          <div className="grid gap-4 sm:grid-cols-2">
            <input value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value })} placeholder="Tag (e.g. Publishing)" data-testid="journal-tag-input" className={inputCls} />
            <label className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">
              <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} data-testid="journal-published-toggle" className="h-4 w-4 accent-[#D4AF37]" />
              Published (visible on site)
            </label>
          </div>
          <textarea value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} placeholder="Short excerpt shown on the card" rows={2} data-testid="journal-excerpt-input" className={inputCls} />
          <textarea required value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Article body — blank line between paragraphs" rows={8} data-testid="journal-body-input" className={`${inputCls} resize-y`} />
          <div className="flex gap-3">
            <button type="submit" data-testid="journal-save-button" className="rounded-full bg-gold px-6 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-navy hover:bg-goldlight">Save</button>
            <button type="button" onClick={() => setDraft(null)} data-testid="journal-cancel-button" className="rounded-full border border-white/25 px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300 hover:border-gold hover:text-gold">Cancel</button>
          </div>
        </form>
      )}

      {posts === null ? (
        <p className="py-16 text-center font-mono text-xs text-slate-400">Loading…</p>
      ) : !posts.length ? (
        <p data-testid="journal-empty" className="py-16 text-center font-serif text-xl italic text-slate-400">No articles yet.</p>
      ) : (
        <div className="space-y-3" data-testid="journal-admin-list">
          {posts.map((p) => (
            <div key={p.id} data-testid={`journal-admin-item-${p.slug}`} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-blueprint/40 p-4 sm:p-5">
              <div className="min-w-0">
                <p className="truncate font-serif text-base font-semibold text-parchment">{p.title}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">
                  {p.tag} · {p.published ? <span className="text-gold">Published</span> : <span className="text-slate-400">Draft</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => togglePublish(p)} data-testid={`journal-publish-toggle-${p.slug}`} aria-label={p.published ? "Unpublish" : "Publish"} className="rounded-full border border-white/20 p-2 text-slate-300 transition-colors hover:border-gold hover:text-gold">
                  {p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button type="button" onClick={() => setDraft({ id: p.id, title: p.title, excerpt: p.excerpt, body: p.body, tag: p.tag, published: p.published })} data-testid={`journal-edit-${p.slug}`} aria-label="Edit article" className="rounded-full border border-white/20 p-2 text-slate-300 transition-colors hover:border-gold hover:text-gold">
                  <Pencil className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => remove(p)} data-testid={`journal-delete-${p.slug}`} aria-label="Delete article" className="rounded-full border border-white/20 p-2 text-slate-300 transition-colors hover:border-red-400 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(null); return; }
    axios.get(`${API}/auth/me`, authHeader())
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); });
  }, []);

  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-navy"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  if (user === null) return <Login onLogin={setUser} />;

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };

  return (
    <div className="min-h-screen bg-navy" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-blueprint/60 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/vhe-logo.png" alt="" className="h-10 w-auto" />
            <div>
              <p className="font-serif text-lg font-semibold text-parchment">Architect&rsquo;s Office</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold/70">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site" className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-gold sm:block">View Site</a>
            <button type="button" onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300 transition-colors hover:border-gold hover:text-gold">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-2 px-4 pb-4 sm:px-6" role="tablist" aria-label="Dashboard sections">
          {[["enquiries", "Enquiries"], ["journal", "Journal"]].map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${tab === id ? "bg-gold text-navy" : "border border-white/20 text-slate-300 hover:border-gold hover:text-gold"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {tab === "enquiries" ? <Enquiries /> : <JournalAdmin />}
      </main>
    </div>
  );
};

export default Admin;
